/* eslint-disable camelcase */
import React, { useState } from "react";
import { Dropdown, DropdownButton, Form, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faAngleUp,
  faEnvelope,
  faMessage,
  faPhone,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useGetBroadcastMessagingReportDataQuery } from "../store/slices/widgetsSlice";
import { Link } from "react-router-dom";
import MyPagination from "../Common/MyPagination";
import useDebouncedValue from "../Common/useDebouncedValue";
import RepliesModal from "../Reporting/RealtimeReporting/RepliesModal";

const columnOptions = [
  { value: "Reference", label: "Reference" },
  // { value: "Label", label: "Label" },
  { value: "CreatedAt", label: "Date" },
  { value: "Email", label: "Sent by" },
  { value: "Dispatched", label: "Sent" },
  { value: "Delivered", label: "Delivered" },
  { value: "Failed", label: "Failed" },
  { value: "Expired", label: "Expired" },
  { value: "Replies", label: "Replies" },
  { value: "Description", label: "Description" },
];

const channelDetails = {
  sms: { icon: faMessage, label: "SMS" },
  email: { icon: faEnvelope, label: "email" },
  voice: { icon: faPhone, label: "voice" },
};

function transactionLink(item, channel, messageStatus) {
  const params = new URLSearchParams({
    broadcastUUID: item.channels[channel].broadcast_uuid,
    startDate: item.CreatedAt,
    comms: channel,
  });
  if (messageStatus) {
    params.set("messageStatus", messageStatus);
  }
  return `/reporting/realtime?${params.toString()}`;
}

function ChannelMetric({ item, metric, messageStatus, onShowReplies }) {
  const channels = item.channels;
  if (!channels) {
    return item[metric] || "—";
  }

  const entries = Object.entries(channels).filter(([, stats]) => stats[metric] > 0);
  if (!entries.length) {
    return "—";
  }

  return entries.map(([channel, stats]) => {
    const { icon, label } = channelDetails[channel];
    const count = stats[metric];
    const description = `${count} ${label} ${metric}`;
    const content = (
      <>
        <FontAwesomeIcon icon={icon} aria-hidden /> <span>{count}</span>
      </>
    );

    return metric === "replies" ? (
      <button
        className="btn btn-link p-0 me-2"
        key={channel}
        type="button"
        onClick={() => onShowReplies(channel)}
        title={`View ${description}`}
        aria-label={`View ${description}`}
      >
        {content}
      </button>
    ) : (
      <Link
        className="me-2"
        key={channel}
        to={transactionLink(item, channel, messageStatus)}
        title={`View ${description} transactions`}
        aria-label={`View ${description} transactions`}
      >
        {content}
      </Link>
    );
  });
}

export default function BroadcastMessaging({ showTransactions = true }) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showReplies, setShowReplies] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    search_field: ["label", "reference", "description"],
    date_gte: "",
    date_lte: "",
  });
  const [order, setOrder] = useState("desc");
  const [order_by, setOrder_by] = useState("created_at");
  const [columnsToShow, setColumnsToShow] = useState(
    localStorage.getItem("BroadcastMessagingOverviewColumns")
      ? JSON.parse(localStorage.getItem("BroadcastMessagingOverviewColumns"))
      : ["report", "Reference", "Label", "CreatedAt", "Email", "Delivered", "Replies"]
  );

  const [page, setPage] = useState(0);
  const [per_page, setPerPage] = useState(10);
  const debouncedSearch = useDebouncedValue(filters.search);

  const {
    data: { broadcasts, total } = { broadcasts: [], total: 0 },
    refetch,
  } =
    useGetBroadcastMessagingReportDataQuery({
      order_by,
      order,
      ...filters,
      search: debouncedSearch,
      page,
      per_page,
    });

  return (
    <div>
      <div className="mb-3">
        <div className="d-flex justify-content-end gap-3 flex-wrap">
          <button type="button" className="btn btn-colored" aria-expanded={showAdvancedFilters} onClick={() => setShowAdvancedFilters((previous) => !previous)}>Filters</button>
          <DropdownButton
            id="show-colums-dropdown"
            title="Show columns"
            variant="colored"
            autoClose="outside"
            align="end"
            className="h-100 rounded-3"
          >
            {columnOptions.map((c) => (
              <Dropdown.Item as="div" key={c.value}>
                <Form.Check
                  checked={columnsToShow.includes(c.value)}
                  label={c.label}
                  id={`${c.value}-checkbox`}
                  onChange={() => {
                    setColumnsToShow((p) => {
                      let newColumns;
                      if (p.includes(c.value)) {
                        newColumns = p.filter((pc) => pc !== c.value);
                      } else {
                        newColumns = [...p, c.value];
                      }
                      localStorage.setItem(
                        "BroadcastMessagingOverviewColumns",
                        JSON.stringify(newColumns)
                      );
                      return newColumns;
                    });
                  }}
                />
              </Dropdown.Item>
            ))}
          </DropdownButton>
          <button
            type="button"
            className="btn btn-sm border-0"
            onClick={refetch}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRotateLeft} />
          </button>
        </div>

      </div>
      {showAdvancedFilters && (
        <div className="asset-filter-panel mb-3 bg-colored-reverse p-3 m-0 rounded-3 text-white"><div className="d-flex flex-column gap-3">
          <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3"><div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>Search</div><div className="d-flex flex-column gap-2 flex-grow-1"><Form.Control placeholder="Search label, reference, or description" value={filters.search} onChange={(event) => { setFilters((previous) => ({ ...previous, search: event.target.value })); setPage(0); }} /><div className="d-flex flex-wrap gap-3">{[["label", "Label"], ["reference", "Reference"], ["description", "Description"]].map(([value, label]) => <Form.Check inline key={value} type="checkbox" id={`broadcast-search-${value}`} label={label} checked={filters.search_field.includes(value)} onChange={() => { setFilters((previous) => ({ ...previous, search_field: previous.search_field.includes(value) ? previous.search_field.filter((field) => field !== value) : [...previous.search_field, value] })); setPage(0); }} />)}</div></div></div>
          <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3"><div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>Sent</div><div className="d-flex flex-wrap gap-3"><Form.Group style={{ width: 250 }}><Form.Label className="small mb-1">From</Form.Label><Form.Control aria-label="Sent from" type="datetime-local" value={filters.date_gte} onChange={(event) => { setFilters((previous) => ({ ...previous, date_gte: event.target.value })); setPage(0); }} /></Form.Group><Form.Group style={{ width: 250 }}><Form.Label className="small mb-1">To</Form.Label><Form.Control aria-label="Sent to" type="datetime-local" value={filters.date_lte} onChange={(event) => { setFilters((previous) => ({ ...previous, date_lte: event.target.value })); setPage(0); }} /></Form.Group></div></div>
          <div><button type="button" className="btn btn-sm btn-outline-light" onClick={() => { setFilters({ search: "", search_field: ["label", "reference", "description"], date_gte: "", date_lte: "" }); setPage(0); }}>Clear filters</button></div>
        </div></div>
      )}

      <Table striped bordered hover>
        <thead>
          <tr>
            {showTransactions && (
              <th>
                <button
                  className="btn btn-link p-0"
                  type="button"
                  onClick={() => {
                    if (order_by !== "Label") {
                      setOrder_by("Label");
                      setOrder("desc");
                    } else {
                      setOrder((p) => (p === "asc" ? "desc" : "asc"));
                    }
                  }}
                >
                  Label
                  {order_by === "Label" && (
                    <FontAwesomeIcon
                      className="ms-2"
                      icon={order === "asc" ? faAngleUp : faAngleDown}
                    />
                  )}
                </button>
              </th>
            )}
            {columnOptions
              .filter((c) => columnsToShow.includes(c.value))
              .map((c) => (
                <th key={c.label}>
                  {/* Sort by column */}
                  <button
                    className="btn btn-link p-0"
                    type="button"
                    onClick={() => {
                      if (order_by !== c.label) {
                        setOrder_by(c.label);
                        setOrder("desc");
                      } else {
                        setOrder((p) => (p === "asc" ? "desc" : "asc"));
                      }
                    }}
                  >
                    {c.label}
                    {order_by === c.label && (
                      <FontAwesomeIcon
                        className="ms-2"
                        icon={order === "asc" ? faAngleUp : faAngleDown}
                      />
                    )}
                  </button>
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {broadcasts.map((b) => (
            <tr key={b.Uuid}>
              {showTransactions && (
                <td>
                  {b.Label}
                  {b.channels &&
                    Object.entries(b.channels)
                      .filter(
                        ([channel, stats]) =>
                          channelDetails[channel] &&
                          ["dispatched", "delivered", "failed", "expired", "replies"].some(
                            (metric) => stats[metric] > 0
                          )
                      )
                      .map(([channel]) => {
                      const { icon, label } = channelDetails[channel];
                      return (
                        <Link
                          to={transactionLink(b, channel)}
                          className="ms-2"
                          key={channel}
                          title={`View ${label} transactions`}
                          aria-label={`View ${label} transactions`}
                        >
                          <FontAwesomeIcon icon={icon} />
                        </Link>
                      );
                      })}
                </td>
              )}
              {columnOptions
                .filter((c) => columnsToShow.includes(c.value))
                .map((c) => (
                  <td key={c.value}>
                    {c.value === "CreatedAt" ? (
                      new Date(b[c.value]).toLocaleString()
                    ) : ["Dispatched", "Delivered", "Failed", "Expired", "Replies"].includes(
                        c.value
                      ) ? (
                      <ChannelMetric
                        item={b}
                        metric={c.value.toLowerCase()}
                        messageStatus={c.value === "Dispatched" ? "sent" : c.value.toLowerCase()}
                        onShowReplies={(channel) =>
                          setShowReplies({
                            channel,
                            context: {
                              kind: "broadcast",
                              value: b.channels[channel].broadcast_uuid,
                              channel,
                            },
                          })
                        }
                      />
                    ) : (
                      b[c.value]
                    )}
                  </td>
                ))}
            </tr>
          ))}
          {!broadcasts.length && (
            <tr>
              <td
                className="text-center text-secondary"
                colSpan={
                  (showTransactions ? 1 : 0) +
                  columnOptions.filter((c) => columnsToShow.includes(c.value))
                    .length
                }
              >
                No broadcast messaging found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <RepliesModal showReplies={showReplies} setShowReplies={setShowReplies} />
      <MyPagination
        total={total}
        onPaginate={(p, pp) => {
          setPage(p);
          setPerPage(pp);
        }}
        perPageOptions={[10, 25, 50]}
        defaultPerPage={10}
        alwaysVisible
      />
    </div>
  );
}
